import Phaser from 'phaser';
import type { DnaSystem } from '@/systems/DnaSystem';
import {
  renderWord,
  renderValue,
  renderLetterList,
  lettersKnown,
} from '@/systems/LiteracyFilter';

/**
 * Genome panel. Always present — a small button top-right toggles a
 * full-screen overlay that shows the blob's accumulated DNA.
 *
 * The panel's CONTENT is scrambled based on the blob's `literacy`
 * DNA value. Early game the player sees shapes of numbers they can't
 * read. As books are consumed, labels clarify and values appear.
 *
 * Literacy → panel readability mapping:
 *   < 0.3  → level 0 (full gibberish, bars only)
 *   0.3-1  → level 1 (partial word reveal)
 *   1-2    → level 2
 *   2-4    → level 3
 *   4+     → level 4 (clear text + exact numbers)
 */
const PANEL_WIDTH_PCT = 0.88;
const PANEL_MAX_ROWS = 14;
const ROW_HEIGHT = 30;
const BAR_WIDTH = 200;
const BAR_HEIGHT = 14;

export class GenomePanel {
  private button: Phaser.GameObjects.Container;
  private overlay: Phaser.GameObjects.Container;
  private isOpen = false;
  private rows: Array<{
    label: Phaser.GameObjects.Text;
    barBg: Phaser.GameObjects.Rectangle;
    bar: Phaser.GameObjects.Rectangle;
    value: Phaser.GameObjects.Text;
  }> = [];
  private title!: Phaser.GameObjects.Text;
  private subtitle!: Phaser.GameObjects.Text;
  private panelBg!: Phaser.GameObjects.Rectangle;
  private backdrop!: Phaser.GameObjects.Rectangle;
  private closeHint!: Phaser.GameObjects.Text;
  private disposers: Array<() => void> = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly dna: DnaSystem,
  ) {
    this.button = this.createButton();
    this.overlay = this.createOverlay();
    this.overlay.setVisible(false);

    // Live-refresh when open and DNA changes
    this.disposers.push(
      dna.onChange(() => {
        if (this.isOpen) this.render();
      }),
    );
  }

  private createButton(): Phaser.GameObjects.Container {
    const mobile = GenomePanel.detectMobile();
    const size = mobile ? 52 : 44;
    const { width } = this.scene.scale;
    const x = width - size / 2 - 20;
    const y = 32;

    const bg = this.scene.add
      .rectangle(0, 0, size, size, 0x0a0a0a, 0.75)
      .setStrokeStyle(1, 0x6cff6c, 0.6);
    const icon = this.scene.add
      .text(0, 0, 'DNA', {
        fontSize: mobile ? '16px' : '14px',
        color: '#9cff9c',
        fontFamily: 'monospace',
      })
      .setOrigin(0.5);

    const hit = this.scene.add
      .rectangle(0, 0, size + 12, size + 12, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerdown', () => this.toggle());

    const container = this.scene.add
      .container(x, y, [bg, icon, hit])
      .setScrollFactor(0, 0)
      .setDepth(1100);
    return container;
  }

  private createOverlay(): Phaser.GameObjects.Container {
    const { width, height } = this.scene.scale;
    const panelW = Math.min(760, width * PANEL_WIDTH_PCT);
    const panelH = Math.min(600, height * 0.82);

    const backdrop = this.scene.add
      .rectangle(width / 2, height / 2, width, height, 0x000000, 0.72)
      .setInteractive();
    backdrop.on('pointerdown', () => this.close());
    this.backdrop = backdrop;

    const panelBg = this.scene.add
      .rectangle(width / 2, height / 2, panelW, panelH, 0x121212, 0.96)
      .setStrokeStyle(2, 0x6cff6c, 0.55);
    // Swallow clicks on the panel itself so they don't close
    panelBg.setInteractive();
    panelBg.on('pointerdown', (p: Phaser.Input.Pointer) => {
      p.event?.stopPropagation?.();
    });
    this.panelBg = panelBg;

    const mobile = GenomePanel.detectMobile();
    const titleSize = mobile ? '30px' : '24px';
    const subtitleSize = mobile ? '16px' : '13px';
    const rowLabelSize = mobile ? '18px' : '15px';
    const rowValueSize = mobile ? '18px' : '15px';

    this.title = this.scene.add
      .text(width / 2, height / 2 - panelH / 2 + 30, 'GENOME', {
        fontSize: titleSize,
        color: '#e8ffe8',
        fontFamily: 'monospace',
      })
      .setOrigin(0.5, 0);

    this.subtitle = this.scene.add
      .text(width / 2, height / 2 - panelH / 2 + 68, '', {
        fontSize: subtitleSize,
        color: '#7a9a7a',
        fontFamily: 'monospace',
      })
      .setOrigin(0.5, 0);

    // Pre-allocate row elements
    const rowsTop = height / 2 - panelH / 2 + 110;
    const barX = width / 2 - 20;
    for (let i = 0; i < PANEL_MAX_ROWS; i++) {
      const y = rowsTop + i * ROW_HEIGHT;
      const label = this.scene.add
        .text(width / 2 - panelW / 2 + 30, y, '', {
          fontSize: rowLabelSize,
          color: '#cfd8cf',
          fontFamily: 'monospace',
        })
        .setOrigin(0, 0.5);
      const barBg = this.scene.add.rectangle(barX, y, BAR_WIDTH, BAR_HEIGHT, 0x1e1e1e, 1).setOrigin(0, 0.5);
      const bar = this.scene.add.rectangle(barX, y, 1, BAR_HEIGHT - 2, 0x6cff6c, 0.9).setOrigin(0, 0.5);
      const value = this.scene.add
        .text(barX + BAR_WIDTH + 16, y, '', {
          fontSize: rowValueSize,
          color: '#9cff9c',
          fontFamily: 'monospace',
        })
        .setOrigin(0, 0.5);
      this.rows.push({ label, barBg, bar, value });
    }

    this.closeHint = this.scene.add
      .text(width / 2, height / 2 + panelH / 2 - 32, 'tap outside to close', {
        fontSize: subtitleSize,
        color: '#555',
        fontFamily: 'monospace',
      })
      .setOrigin(0.5, 0);

    const children: Phaser.GameObjects.GameObject[] = [
      backdrop,
      panelBg,
      this.title,
      this.subtitle,
      this.closeHint,
    ];
    for (const r of this.rows) {
      children.push(r.label, r.barBg, r.bar, r.value);
    }

    const container = this.scene.add
      .container(0, 0, children)
      .setScrollFactor(0, 0)
      .setDepth(1200);
    return container;
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(): void {
    this.isOpen = true;
    this.overlay.setVisible(true);
    this.render();
  }

  close(): void {
    this.isOpen = false;
    this.overlay.setVisible(false);
  }

  render(): void {
    const literacy = this.dna.get('literacy');
    const dominant = this.dna.dominant().slice(0, PANEL_MAX_ROWS);
    const maxVal = dominant.reduce((m, t) => Math.max(m, t.value), 0.001);

    this.subtitle.setText(this.subtitleFor(literacy));

    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i];
      const entry = dominant[i];
      if (!entry) {
        row.label.setText('');
        row.value.setText('');
        row.bar.setVisible(false);
        row.barBg.setVisible(false);
        continue;
      }

      row.bar.setVisible(true);
      row.barBg.setVisible(true);

      // Bar length is always proportional — you always feel the shape of yourself
      const pct = Math.min(1, entry.value / maxVal);
      row.bar.setSize(Math.max(2, BAR_WIDTH * pct), BAR_HEIGHT - 2);

      row.label.setText(renderWord(entry.category, literacy));
      row.value.setText(renderValue(entry.value, literacy));
    }
  }

  destroy(): void {
    for (const d of this.disposers) d();
    this.disposers = [];
    this.button.destroy();
    this.overlay.destroy();
  }

  private subtitleFor(literacy: number): string {
    const known = lettersKnown(literacy).size;
    if (known === 0) return renderWord('traits accumulated', literacy, 'subtitle');
    if (known >= 26) return 'traits accumulated from consumption';
    const letters = renderLetterList(literacy);
    return `letters known: ${letters}`;
  }

  private static detectMobile(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(max-width: 900px), (pointer: coarse)').matches ?? false;
  }
}
